import { createSlice } from '@reduxjs/toolkit';

// utils
import axios, { endpoints } from '../../utils/axios'; // import dispatch from store
import { getPlatformIconUrl } from '../../utils/platform-icon';
import { voolistExtension } from '../../utils/voolist-extension';
import { trackEvent, ANALYTICS_EVENTS } from '../../utils/posthog';
import { depopCategoryOptions, findDepopCategoryTree } from '../../utils/depop-reference-data';
import marketplaceConnection, { connectNewEbayAccount, connectNewEtsyAccount } from './integrations';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: null,
  marketplaces: [],
  ebayCategories: [],
  ebayRequiredFields: [],
  ebayOptionalFields: [],
  ebayPoliciesAndLocations: {},
  ebayCountryAndUrl: {},
  ebayShippingServices: {
    domesticServices: [],
    internationalServices: []
  },
  ebayShippingServicesByAccount: {},  // { '42': { domesticServices: [...], internationalServices: [...] } }
  etsyCategories: [],
  etsyCategoriesError: null,
  etsyCategoriesLoading: false,
  etsyRequiredFields: [],
  etsyOptionalFields: [],
  etsyShippingAndReturnPolicies: {},
  integrations: [],
  depopAttributes: null,          // { colours, brands } from Depop's attributes list
  // Depop's category tree for the pickers (see utils/depop-category-tree). `status` is 'idle' until
  // it is asked for, then 'loading', 'loaded', or 'unavailable' when it could not be found.
  depopCategoryTree: { status: 'idle', departments: [], productTypes: {} },
  poshmarkCatalog: null,
  poshmarkDepartments: [],
  poshmarkStyleTags: [],

  // Per-account state for multi-account listing form
  categoriesByTree: {},           // { '0': [...eBay US cats], '77': [...eBay DE cats] }
  categoryFieldsByAccount: {},    // { '42:123456': { required: [...], optional: [...], categoryTreeId: 0 } }
  etsyCategoryFieldsByAccount: {}, // { '42:123456': { required: [...], optional: [...] } }
  itemConditionsByAccount: {},    // { '42:123456': [...conditions] }
  compatibilityByAccount: {},     // { '42:57357': { supported, properties } } — eBay Motors fitment
  policiesByAccount: {},          // { 42: { fulfillmentPolicy: [...], ... } }
  // The eBay accounts whose policies the seller synced from eBay (Sync Policies) since the app loaded:
  // their policiesByAccount entry is eBay's own list, so a policy missing from it is gone from eBay.
  ebayPoliciesSynced: {},         // { 42: true }
  storeCategoriesByAccount: {},   // { 42: { hasStore: true, categories: [...] } } — eBay Store categories
  shopSectionsByAccount: {},      // { 383: { sections: [{ value, title }] } } — Etsy shop sections
  productCategoriesByAccount: {}, // { 387: { categories: [...tree], manageUrl } | { failed: true } } — WooCommerce/BigCommerce
  accountLoadingStates: {},       // { 42: { categories: false, policies: false, fields: false } }
};

const slice = createSlice({
  name: 'management',
  initialState,
  reducers: {
    // START LOADING
    startLoading (state) {
      state.isLoading = true;
    },
    // END LOADING
    endLoading (state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError (state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    // RESET ERROR
    resetError (state, action) {
      state.isLoading = false;
      state.error = null;
    },

    // GET MARKETPLACES
    getMarketplacesSuccess (state, action) {
      const marketplaces = action.payload;
      state.marketplaces = marketplaces;
      state.isLoading = false;

      // Pre-populate policiesByAccount from each account's embedded policies
      marketplaces
        .filter(mp => mp.accounts)
        .flatMap(mp => mp.accounts)
        .forEach(acct => {
          if (acct.integrationId && acct.policies && Object.keys(acct.policies).length > 0) {
            state.policiesByAccount[acct.integrationId] = acct.policies;
          }
        });
    },

    // GET EBAY CATEGORIES
    getEbayCategoriesSuccess (state, action) {
      const ebayCategories = action.payload;
      state.ebayCategories = ebayCategories;
      state.isLoading = false;
    },

    // GET EBAY REQUIRED FIELDS
    getEbayCategoryRequiredFieldsSuccess (state, action) {
      const ebayRequiredCategoryFields = action.payload;
      state.ebayRequiredFields = ebayRequiredCategoryFields;
      state.isLoading = false;
    },
    // GET EBAY OPTIONAL FIELDS
    getEbayCategoryOptionalFieldsSuccess (state, action) {
      const ebayOptionalCategoryFields = action.payload;
      state.ebayOptionalFields = ebayOptionalCategoryFields;
      state.isLoading = false;
    },


    // GET ETSY CATEGORIES
    getEtsyCategoriesSuccess (state, action) {
      const etsyCategories = action.payload;
      state.etsyCategories = etsyCategories;
      state.isLoading = false;
      state.etsyCategoriesLoading = false;
    },


    // GET ETSY REQUIRED FIELDS
    getEtsyCategoryRequiredFieldsSuccess (state, action) {
      const etsyCategoryRequiredFields = action.payload;
      state.etsyRequiredFields = etsyCategoryRequiredFields;
      state.isLoading = false;
    },


    // GET ETSY CATEGORY FIELDS
    getEtsyCategoryOptionalFieldsSuccess (state, action) {
      const etsyCategoryOptionalFields = action.payload;
      state.etsyOptionalFields = etsyCategoryOptionalFields;
      state.isLoading = false;
    },

    // GET EBAY POLICIES AND LOCATIONS
    fetchEbayPoliciesAndLocationsSuccess (state, action) {
      const ebayPoliciesAndLocations = action.payload;
      state.ebayPoliciesAndLocations = ebayPoliciesAndLocations;
      state.isLoading = false;
    },


    // GET ETSY SHIPPING AND RETURN POLICIES
    fetchEtsyShippingAndReturnPoliciesSuccess (state, action) {
      const etsyShippingAndReturnPolicies = action.payload;
      state.etsyShippingAndReturnPolicies = etsyShippingAndReturnPolicies;
      state.isLoading = false;
    },
    // GET EBAY COUNTRY AND URL
    getEbayCountryAndUrlSuccess (state, action) {
      const ebayCountryAndUrl = action.payload;
      state.ebayCountryAndUrl = ebayCountryAndUrl;
      state.isLoading = false;
    },
    // GET EBAY SHIPPING SERVICES (legacy global)
    getEbayShippingServicesSuccess (state, action) {
      const ebayShippingServices = action.payload;
      state.ebayShippingServices = ebayShippingServices;
    },
    // GET EBAY SHIPPING SERVICES (per-account)
    setEbayShippingServicesByAccount (state, action) {
      const { integrationId, services } = action.payload;
      state.ebayShippingServicesByAccount[integrationId] = services;
    },
    // GET INTEGRATIONS
    getIntegrationsSuccess (state, action) {
      const integrations = action.payload;
      state.integrations = integrations;
      state.isLoading = false;
    },

    // GET DEPOP ATTRIBUTES
    getDepopAttributesSuccess (state, action) {
      state.depopAttributes = action.payload;
    },

    // DEPOP CATEGORY TREE
    depopCategoryTreeLoading (state) {
      state.depopCategoryTree.status = 'loading';
    },
    depopCategoryTreeLoaded (state, action) {
      state.depopCategoryTree = { status: 'loaded', ...action.payload };
    },
    depopCategoryTreeUnavailable (state) {
      state.depopCategoryTree = { status: 'unavailable', departments: [], productTypes: {} };
    },

    // GET POSHMARK CATALOG
    getPoshmarkCatalogSuccess (state, action) {
      state.poshmarkCatalog = action.payload.catalog;
      state.poshmarkDepartments = action.payload.departments;
      state.poshmarkStyleTags = action.payload.styleTags;
    },

    // Per-account reducers for multi-account listing form
    setCategoriesByTree (state, action) {
      const { treeId, categories } = action.payload;
      state.categoriesByTree[treeId] = categories;
    },
    setCategoryFieldsByAccount (state, action) {
      // key = `${integrationId}:${categoryId}`. categoryTreeId names the eBay site the fields are for
      // (0 = eBay US): which values an item specific takes depends on it.
      const { key, required, optional, categoryTreeId } = action.payload;
      state.categoryFieldsByAccount[key] = { required, optional, categoryTreeId };
    },
    setEtsyCategoryFieldsByAccount (state, action) {
      const { key, required, optional } = action.payload; // key = `${integrationId}:${categoryId}`
      state.etsyCategoryFieldsByAccount[key] = { required, optional };
    },
    setItemConditionsByAccount (state, action) {
      const { key, conditions } = action.payload; // key = `${integrationId}:${categoryId}`
      state.itemConditionsByAccount[key] = conditions;
    },
    setCompatibilityByAccount (state, action) {
      const { key, supported, properties } = action.payload; // key = `${integrationId}:${categoryId}`
      state.compatibilityByAccount[key] = { supported, properties };
    },
    setPoliciesByAccount (state, action) {
      const { integrationId, policies } = action.payload;
      state.policiesByAccount[integrationId] = policies;
    },
    ebayPoliciesSynced (state, action) {
      state.ebayPoliciesSynced[action.payload.integrationId] = true;
    },
    setStoreCategoriesByAccount (state, action) {
      const { integrationId, data } = action.payload; // data = { hasStore, categories }
      state.storeCategoriesByAccount[integrationId] = data;
    },
    setShopSectionsByAccount (state, action) {
      const { integrationId, data } = action.payload; // data = { sections }
      state.shopSectionsByAccount[integrationId] = data;
    },
    setProductCategoriesByAccount (state, action) {
      const { integrationId, data } = action.payload; // data = { categories, manageUrl } or { failed: true }
      state.productCategoriesByAccount[integrationId] = data;
    },
    setAccountLoading (state, action) {
      const { integrationId, field, loading } = action.payload;
      if (!state.accountLoadingStates[integrationId]) {
        state.accountLoadingStates[integrationId] = {};
      }
      state.accountLoadingStates[integrationId][field] = loading;
    },
    setAccountError (state, action) {
      const { integrationId, field, error } = action.payload;
      if (!state.accountLoadingStates[integrationId]) {
        state.accountLoadingStates[integrationId] = {};
      }
      state.accountLoadingStates[integrationId][`${field}Error`] = error;
    },
    setEtsyCategoriesError (state, action) {
      state.etsyCategoriesError = action.payload;
    },
    setEtsyCategoriesLoading (state, action) {
      state.etsyCategoriesLoading = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------
export function getIntegrations () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const URL = endpoints.auth.integrations;
    const { data } = await axios.get(URL);
    dispatch(slice.actions.getIntegrationsSuccess(data));
  };
}


// ----------------------------------------------------------------------

export function getMarketplaces () {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {

      const URL = endpoints?.management?.marketplaces;
      const { data } = await axios.get(URL);
      // Extension-based platforms that need the marketplace name passed to the connect function
      const EXTENSION_PLATFORMS = ['vinted', 'poshmark', 'mercari', 'depop', 'grailed', 'whatnot', 'offerup', 'vestiaire'];

      const marketplaces = data?.map((marketplace) => ({
        ...marketplace,
        image: getPlatformIconUrl(marketplace?.platform),
        // For extension platforms, wrap the action to pass the marketplace name
        action: EXTENSION_PLATFORMS.includes(marketplace?.platform)
          ? () => marketplaceConnection[marketplace?.platform]?.(marketplace?.name)
          : marketplaceConnection[marketplace?.platform],
        revokeIntegration: () => marketplaceConnection.revokeIntegration(marketplace?.name),
      })
      );

      // Expand non-extension platforms: for connected entries, add an "add another account" card.
      // Always show the card (even at plan limit) so users see an upgrade prompt instead of nothing.
      const addNewCardAdded = new Set();
      const expanded = marketplaces.flatMap((mp, _idx, arr) => {
        if (!mp.connected || mp.extensionBased) return [mp];

        // For eBay sub-stores, dedup by sub-store name (ebayUs, ebayDE each get their own card).
        // For other platforms, dedup by platform.
        const dedupKey = mp.ebaySubStore ? mp.name : mp.platform;
        if (addNewCardAdded.has(dedupKey)) return [mp];

        // Only attach the card to the last connected entry for this group
        const lastConnected = [...arr].reverse().find(
          m => m.connected && (mp.ebaySubStore ? m.name === mp.name : (m.platform === mp.platform && !m.ebaySubStore))
        );
        if (lastConnected && lastConnected !== mp) return [mp];

        addNewCardAdded.add(dedupKey);

        const atLimit = (mp.accounts?.length || 0) >= (mp.maxAccounts || 1);

        // OAuth platforms (eBay, Etsy) need mode=connect_new to create a new integration.
        // Modal platforms (Shopify, WooCommerce, BigCommerce) use the same action —
        // the user enters different store details, which naturally creates a new integration.
        let addNewAction = mp.action;
        if (mp.platform === 'ebay') addNewAction = connectNewEbayAccount;
        else if (mp.platform === 'etsy') addNewAction = connectNewEtsyAccount;

        return [mp, {
          ...mp,
          // The tile is for another account, so it carries the marketplace's name, not this account's.
          label: mp.marketplaceLabel || mp.label,
          connected: false,
          integrationId: null,
          updatedAt: null,
          createdAt: null,
          autoDelistEnabled: true,
          autoOfferSettings: null,
          shop: null,
          action: addNewAction,
          revokeIntegration: mp.revokeIntegration,
          _cardKey: `${mp.name}-add-new`,
          atLimit,
          maxAccounts: mp.maxAccounts || 1,
        }];
      });

      dispatch(slice.actions.getMarketplacesSuccess(expanded));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function getEbayCategories () {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const URL = endpoints.management.ebay.category;
      const { data } = await axios.get(URL);

      // Backend now returns transformed categories directly
      dispatch(slice.actions.getEbayCategoriesSuccess(data || []));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw new Error(error.message);
    }
  };
}

// ----------------------------------------------------------------------
export function getEbayCategoryFields (category_id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());

      if (!category_id) {
        dispatch(slice.actions.getEbayCategoryRequiredFieldsSuccess([]));
        dispatch(slice.actions.getEbayCategoryOptionalFieldsSuccess([]));
        return;
      }

      const res = await axios.post(endpoints.management.ebay.categoryFields, {
        category_id
      });

      const { requiredFields, optionalFields } = res?.data || { requiredFields: [], optionalFields: [] };

      dispatch(slice.actions.getEbayCategoryRequiredFieldsSuccess(requiredFields));
      dispatch(slice.actions.getEbayCategoryOptionalFieldsSuccess(optionalFields));

    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

export async function getEbayItemCondition (category_id) {
  const res = await axios.get(`${endpoints.management.ebay.itemCondition}/${category_id}`);
  return res.data;
}

// Helper to append integrationId query param when present
const withIntegrationId = (url, integrationId) =>
  integrationId ? `${url}${url.includes('?') ? '&' : '?'}integrationId=${integrationId}` : url;

// ----------------------------------------------------------------------
export function fetchEbayPoliciesAndLocations (integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get(withIntegrationId(endpoints.management.ebay.policies.fetch, integrationId))
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(res.data));
      if (integrationId) {
        dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: res.data }));
        dispatch(slice.actions.ebayPoliciesSynced({ integrationId }));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}
// ----------------------------------------------------------------------
export function getEbayPoliciesAndLocations (integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get(withIntegrationId(endpoints.management.ebay.policies.get, integrationId))
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(res.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: res.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function savePostalCodeForAccount (integrationId, postalCode) {
  return async (dispatch) => {
    try {
      const response = await axios.post(withIntegrationId(endpoints.management.ebay.postalCode, integrationId), { postalCode });
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_LOCATION_SAVED, { platform: 'ebay', action: 'postal_code' });
      return response.data;
    } catch (error) {
      // Background save — log but don't block the user
      console.error('Failed to save postal code', error?.response?.data?.message || error?.message);
      return null;
    }
  };
}

// ----------------------------------------------------------------------
export function createEbayLocation (location, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      // Create location and get updated policies in one request
      const response = await axios.post(withIntegrationId(endpoints.management.ebay.location, integrationId), location);
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_LOCATION_SAVED, { platform: 'ebay', action: 'create' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function updateEbayLocation (merchantLocationKey, locationData, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.put(withIntegrationId(`${endpoints.management.ebay.location}/${encodeURIComponent(merchantLocationKey)}`, integrationId), locationData);
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_LOCATION_SAVED, { platform: 'ebay', action: 'update' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function deleteEbayLocation (merchantLocationKey, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.delete(withIntegrationId(`${endpoints.management.ebay.location}/${encodeURIComponent(merchantLocationKey)}`, integrationId));
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_LOCATION_SAVED, { platform: 'ebay', action: 'delete' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
// eBay business-policy CRUD (fulfillment / payment / return).
// policyType is 'fulfillment' | 'payment' | 'return'. The backend persists
// optimistically and returns the full updated policies object, which we store
// directly (same shape the fetch thunk produces).
export function createEbayPolicy (policyType, data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.post(withIntegrationId(`${endpoints.management.ebay.policy}/${policyType}`, integrationId), data);
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'ebay', policyType, action: 'create' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function updateEbayPolicy (policyType, policyId, data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.put(withIntegrationId(`${endpoints.management.ebay.policy}/${policyType}/${encodeURIComponent(policyId)}`, integrationId), data);
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'ebay', policyType, action: 'update' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function deleteEbayPolicy (policyType, policyId, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.delete(withIntegrationId(`${endpoints.management.ebay.policy}/${policyType}/${encodeURIComponent(policyId)}`, integrationId));
      dispatch(slice.actions.fetchEbayPoliciesAndLocationsSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'ebay', policyType, action: 'delete' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function getEbayCountryAndUrl (integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get(withIntegrationId(endpoints.management.ebay.details, integrationId));
      dispatch(slice.actions.getEbayCountryAndUrlSuccess(res.data));
    } catch (error) {
      // Don't log 404 errors as they're expected when eBay isn't connected
      // Only log actual server errors (5xx) or unexpected errors
      if (error?.response?.status !== 404) {
        dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      } else {
        // Silently handle 404 - eBay integration not found
        dispatch(slice.actions.endLoading());
      }
    }
  };
}

// ----------------------------------------------------------------------
export function getEbayShippingServices (integrationId) {
  return async (dispatch, getState) => {
    if (!integrationId) return;
    // Dedup: skip if already fetched for this account
    if (getState().management.ebayShippingServicesByAccount?.[integrationId]) return;
    try {
      const res = await axios.get(withIntegrationId(endpoints.management.ebay.shippingServices, integrationId));
      // Store per-account
      dispatch(slice.actions.setEbayShippingServicesByAccount({ integrationId, services: res.data }));
      // Also update legacy global for backward compat
      dispatch(slice.actions.getEbayShippingServicesSuccess(res.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function getEtsyCategories () {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setEtsyCategoriesLoading(true));
      dispatch(slice.actions.setEtsyCategoriesError(null));
      const res = await axios.get(`${endpoints.management.etsy.category}`);

      dispatch(slice.actions.getEtsyCategoriesSuccess(res.data || []));
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Couldn't load Etsy categories. Try again in a moment.";
      dispatch(slice.actions.setEtsyCategoriesLoading(false));
      dispatch(slice.actions.setEtsyCategoriesError(message));
    }
  };
}


// ----------------------------------------------------------------------
export function getEtsyCategoryFields (category_id) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      if (!category_id) {
        dispatch(slice.actions.getEtsyCategoryRequiredFieldsSuccess([]));
        dispatch(slice.actions.getEtsyCategoryOptionalFieldsSuccess([]));
        return;
      }

      const res = await axios.get(`${endpoints.management.etsy.categoryFields}/${category_id}`);

      const { requiredFields, optionalFields } = res?.data || { requiredFields: [], optionalFields: [] };
      dispatch(slice.actions.getEtsyCategoryRequiredFieldsSuccess(requiredFields));
      dispatch(slice.actions.getEtsyCategoryOptionalFieldsSuccess(optionalFields));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

/**
 * Fetch Etsy category fields for an account + category combo.
 * Keyed by `${integrationId}:${categoryId}` for per-account separation.
 */
export function getEtsyCategoryFieldsForAccount (integrationId, categoryId) {
  return async (dispatch, getState) => {
    const key = `${integrationId}:${categoryId}`;
    // Dedup: skip if already fetched
    if (getState().management.etsyCategoryFieldsByAccount[key]) return;
    if (!categoryId) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'fields', loading: true }));
    try {
      const res = await axios.get(`${endpoints.management.etsy.categoryFields}/${categoryId}`);
      const { requiredFields, optionalFields } = res?.data || { requiredFields: [], optionalFields: [] };
      dispatch(slice.actions.setEtsyCategoryFieldsByAccount({ key, required: requiredFields, optional: optionalFields }));
    } catch (error) {
      console.error(`Failed to fetch Etsy category fields for ${key}:`, error);
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'fields', loading: false }));
    }
  };
}
// ----------------------------------------------------------------------
// Etsy policy CRUD (return policies + shipping profiles). The backend persists
// optimistically and returns the full updated policies object, stored directly.
export function createEtsyReturnPolicy (data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.post(withIntegrationId(endpoints.management.etsy.returnPolicy, integrationId), data);
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'etsy', policyType: 'return', action: 'create' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function updateEtsyReturnPolicy (returnPolicyId, data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.put(withIntegrationId(`${endpoints.management.etsy.returnPolicy}/${returnPolicyId}`, integrationId), data);
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'etsy', policyType: 'return', action: 'update' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function deleteEtsyReturnPolicy (returnPolicyId, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.delete(withIntegrationId(`${endpoints.management.etsy.returnPolicy}/${returnPolicyId}`, integrationId));
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_POLICY_SAVED, { platform: 'etsy', policyType: 'return', action: 'delete' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function createEtsyShippingProfile (data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.post(withIntegrationId(endpoints.management.etsy.shippingProfile, integrationId), data);
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function updateEtsyShippingProfile (shippingProfileId, data, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.put(withIntegrationId(`${endpoints.management.etsy.shippingProfile}/${shippingProfileId}`, integrationId), data);
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_SHIPPING_PROFILE_SAVED, { platform: 'etsy', action: 'update' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function deleteEtsyShippingProfile (shippingProfileId, integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const response = await axios.delete(withIntegrationId(`${endpoints.management.etsy.shippingProfile}/${shippingProfileId}`, integrationId));
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(response.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: response.data }));
      trackEvent(ANALYTICS_EVENTS.SETTINGS_SHIPPING_PROFILE_SAVED, { platform: 'etsy', action: 'delete' });
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}
// ----------------------------------------------------------------------
export function fetchEtsyShippingAndReturnPolicies (integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get(withIntegrationId(endpoints.management.etsy.policies.fetch, integrationId));
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(res.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: res.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function getEtsyPoliciesAndLocations (integrationId) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get(withIntegrationId(endpoints.management.etsy.policies.get, integrationId));
      dispatch(slice.actions.fetchEtsyShippingAndReturnPoliciesSuccess(res.data));
      if (integrationId) dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: res.data }));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}

// ----------------------------------------------------------------------
export function createSettings (settings) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(endpoints.management.settings, settings);
      dispatch(slice.actions.endLoading());
      trackEvent(ANALYTICS_EVENTS.SETTINGS_STORE_PREFERENCES_SAVED);
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };
}
//----------------------------------------------------------------------
export function resetError () {
  return async (dispatch) => {
    dispatch(slice.actions.resetError());
  };
}

// ----------------------------------------------------------------------
/**
 * Load Depop's category tree for the pickers, from wherever findDepopCategoryTree finds it: the
 * tree the server keeps, or else the seller's browser, whose answer is then handed to the server for
 * the next seller. The tree's status in the store says how it went, which is what every screen reads.
 *
 * @param {string} surface - the screen asking, for the analytics event sent when the tree is missing
 */
export function loadDepopCategoryTree (surface) {
  return async (dispatch) => {
    dispatch(slice.actions.depopCategoryTreeLoading());

    const tree = await findDepopCategoryTree({
      readKept: async () => (await axios.get(endpoints.management.depop.categoryFilters)).data,
      askBrowser: async () => {
        // Asking an extension that is not there only ends when the request times out.
        if (!(await voolistExtension.isInstalled())) return null;
        const answer = await voolistExtension.getDepopCategoryFilters();
        return answer?.success ? (answer.data?.data || answer.data) : null;
      },
      keep: (fetched) => axios.post(endpoints.management.depop.categoryFilters, fetched),
    });
    const options = tree ? depopCategoryOptions(tree) : null;

    if (!options?.departments.length) {
      dispatch(slice.actions.depopCategoryTreeUnavailable());
      trackEvent(ANALYTICS_EVENTS.DEPOP_CATEGORIES_UNAVAILABLE, { surface });
      return;
    }
    dispatch(slice.actions.depopCategoryTreeLoaded(options));
  };
}

/**
 * Normalize Depop's attributes list (GET webapi.depop.com/presentation/api/v1/attributes/) into
 * the colours and brands the Depop section offers. Works for the extension bridge's answer and the
 * backend's alike.
 */
function normalizeDepopAttributes (src) {
  const colours = (src.colour?.attributes || [])
    .map(a => ({ id: a.id, label: a.name_i18n?.en || a.name_i18n?.['en-US'] || a.id }));

  const brands = Array.isArray(src.brand)
    ? src.brand.map(b => ({ id: b.id, label: b.name || b.id }))
    : [];

  return { colours, brands };
}

/** Load Depop's colours and brands: from the seller's browser first, then from the backend. */
export function getDepopAttributes () {
  return async (dispatch) => {
    try {
      let attrData = null;

      try {
        if (await voolistExtension.isInstalled()) {
          const extResult = await voolistExtension.getDepopAttributes();
          if (extResult?.success && extResult?.data) {
            attrData = normalizeDepopAttributes(extResult.data?.data || extResult.data);
          }
        }
      } catch (_extAttrErr) { /* extension not available */ }

      if (!attrData) {
        try {
          const res = await axios.get(endpoints.management.depop.attributes);
          attrData = normalizeDepopAttributes(res.data?.data || res.data);
        } catch (_attrErr) { /* backend not available */ }
      }

      dispatch(slice.actions.getDepopAttributesSuccess({
        colours: attrData?.colours || [],
        brands: attrData?.brands || [],
      }));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------
export function getPoshmarkCatalog () {
  return async (dispatch) => {
    try {
      const res = await axios.get(endpoints.management.poshmark.catalog);
      const { catalog: rawCatalog, styleTags: rawStyleTags } = res.data;

      let catalog = null;
      let departments = [];
      let styleTags = [];

      if (rawCatalog) {
        catalog = rawCatalog.catalog || rawCatalog;
        const depts = catalog.departments || catalog.department || [];
        departments = (Array.isArray(depts) ? depts : Object.values(depts))
          .map(d => ({
            id: d.id || d._id,
            label: d.display || d.name || d.title || d.id,
            categories: d.categories || d.category || [],
          }));
      }

      if (rawStyleTags) {
        styleTags = (Array.isArray(rawStyleTags) ? rawStyleTags : rawStyleTags.style_tags || rawStyleTags.tags || [])
          .map(t => typeof t === 'string' ? t : (t.display || t.name || t.tag || t.id));
      }

      dispatch(slice.actions.getPoshmarkCatalogSuccess({ catalog, departments, styleTags }));
    } catch (error) {
      dispatch(slice.actions.hasError(error?.response?.data?.message || error?.message));
      throw error;
    }
  };
}

// ======================================================================
// Per-account thunks for multi-account listing form
// ======================================================================

/**
 * Fetch eBay categories for an account, deduped by categoryTreeId.
 * Two eBay US accounts share treeId "0" — only one API call is made.
 */
export function getEbayCategoriesForAccount (integrationId, categoryTreeId) {
  return async (dispatch, getState) => {
    // Dedup: skip if we already have categories for this tree
    const existing = getState().management.categoriesByTree[categoryTreeId];
    if (existing && existing.length > 0) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'categories', loading: true }));
    dispatch(slice.actions.setAccountError({ integrationId, field: 'categories', error: null }));
    try {
      const base = withIntegrationId(endpoints.management.ebay.category, integrationId);
      // Forward the tree ONLY when it's a real numeric tree (e.g. 100 for eBay Motors, 0 for US).
      // The cache key can be a non-numeric fallback ("int-42"); never send that — the backend would
      // parseInt → NaN. Omitting it lets the backend default to the account's own tree (unchanged).
      const numericTree = Number(categoryTreeId);
      const url = Number.isFinite(numericTree)
        ? `${base}${base.includes('?') ? '&' : '?'}categoryTreeId=${numericTree}`
        : base;
      const { data } = await axios.get(url);
      dispatch(slice.actions.setCategoriesByTree({ treeId: categoryTreeId, categories: data || [] }));
    } catch (error) {
      console.error(`Failed to fetch eBay categories for tree ${categoryTreeId}:`, error);
      const message = error?.response?.data?.message || error?.message || "Couldn't load eBay categories. Try again in a moment.";
      dispatch(slice.actions.setAccountError({ integrationId, field: 'categories', error: message }));
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'categories', loading: false }));
    }
  };
}

/**
 * Fetch this account's eBay STORE categories (the seller's own storefront categories, not the
 * marketplace tree). Keyed by integrationId — every store is account-specific. The backend
 * answers { hasStore: false, categories: [] } for accounts without a store subscription (a
 * normal state, cached server-side); a transient failure leaves the state entry unset, which
 * the form treats as "unknown" and simply hides the pickers without touching stored values.
 */
export function getEbayStoreCategoriesForAccount (integrationId, { force = false } = {}) {
  return async (dispatch, getState) => {
    // Dedup: skip if already fetched (unless forced) — both real trees and "no store" answers.
    if (!force && getState().management.storeCategoriesByAccount[integrationId]) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'storeCategories', loading: true }));
    try {
      const base = withIntegrationId(endpoints.management.ebay.storeCategories, integrationId);
      const url = force ? `${base}${base.includes('?') ? '&' : '?'}refresh=true` : base;
      const { data } = await axios.get(url);
      dispatch(slice.actions.setStoreCategoriesByAccount({ integrationId, data: data || { hasStore: false, categories: [] } }));
    } catch (error) {
      console.error(`Failed to fetch eBay store categories for account ${integrationId}:`, error);
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'storeCategories', loading: false }));
    }
  };
}

/**
 * Fetch this account's Etsy SHOP SECTIONS (the groupings the seller made inside their own Etsy
 * shop). Keyed by integrationId — every shop's sections are its own. A shop without sections
 * answers { sections: [] } (a normal state, cached server-side); a transient failure leaves the
 * state entry unset, which the form treats as "unknown" and simply hides the picker without
 * touching the stored section.
 */
export function getEtsyShopSectionsForAccount (integrationId, { force = false } = {}) {
  return async (dispatch, getState) => {
    // Dedup: skip if already fetched (unless forced) — both real lists and empty answers.
    if (!force && getState().management.shopSectionsByAccount[integrationId]) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'shopSections', loading: true }));
    try {
      const base = withIntegrationId(endpoints.management.etsy.shopSections, integrationId);
      const url = force ? `${base}${base.includes('?') ? '&' : '?'}refresh=true` : base;
      const { data } = await axios.get(url);
      dispatch(slice.actions.setShopSectionsByAccount({ integrationId, data: data || { sections: [] } }));
    } catch (error) {
      console.error(`Failed to fetch Etsy shop sections for account ${integrationId}:`, error);
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'shopSections', loading: false }));
    }
  };
}

/**
 * Fetch the PRODUCT CATEGORIES of this account's WooCommerce or BigCommerce store (the categories
 * the seller made in their own store). Keyed by integrationId — every store's categories are its
 * own. A store without categories answers { categories: [] } (a normal state, cached server-side).
 * A failed read is kept as { failed: true } so the form can offer "Try again"; the saved
 * categories are left untouched, and the next visit asks the store again.
 */
export function getProductCategoriesForAccount (integrationId, { force = false } = {}) {
  return async (dispatch, getState) => {
    const known = getState().management.productCategoriesByAccount[integrationId];
    if (!force && known && !known.failed) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'productCategories', loading: true }));
    try {
      const base = withIntegrationId(endpoints.management.productCategories, integrationId);
      const url = force ? `${base}&refresh=true` : base;
      const { data } = await axios.get(url);
      dispatch(slice.actions.setProductCategoriesByAccount({ integrationId, data: { categories: data?.categories || [], manageUrl: data?.manageUrl || null } }));
    } catch (error) {
      console.error(`Failed to fetch store categories for account ${integrationId}:`, error);
      dispatch(slice.actions.setProductCategoriesByAccount({ integrationId, data: { failed: true } }));
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'productCategories', loading: false }));
    }
  };
}

/**
 * Fetch eBay category fields (item specifics) for an account + category combo.
 * Keyed by `${integrationId}:${categoryId}` for per-account separation.
 */
export function getCategoryFieldsForAccount (integrationId, categoryId, categoryTreeId) {
  return async (dispatch, getState) => {
    const key = `${integrationId}:${categoryId}`;
    // Dedup: skip if already fetched
    if (getState().management.categoryFieldsByAccount[key]) return;
    if (!categoryId) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'fields', loading: true }));
    try {
      const res = await axios.post(withIntegrationId(endpoints.management.ebay.categoryFields, integrationId), {
        category_id: categoryId,
        // Forward the tree so eBay Motors (tree 100) aspects come from the Motors marketplace.
        ...(categoryTreeId !== undefined && categoryTreeId !== null ? { categoryTreeId } : {})
      });
      const { requiredFields, optionalFields, categoryTreeId: answeringTree } = res?.data || { requiredFields: [], optionalFields: [] };
      dispatch(slice.actions.setCategoryFieldsByAccount({ key, required: requiredFields, optional: optionalFields, categoryTreeId: answeringTree }));
    } catch (error) {
      console.error(`Failed to fetch category fields for ${key}:`, error);
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'fields', loading: false }));
    }
  };
}

/**
 * Fetch eBay item conditions for an account + category combo.
 */
export function getItemConditionsForAccount (integrationId, categoryId, categoryTreeId) {
  return async (dispatch, getState) => {
    const key = `${integrationId}:${categoryId}`;
    if (getState().management.itemConditionsByAccount[key]) return;
    if (!categoryId) return;

    try {
      // Forward the tree so eBay Motors categories (tree 100) fetch conditions from EBAY_MOTORS_US.
      const base = withIntegrationId(`${endpoints.management.ebay.itemCondition}/${categoryId}`, integrationId);
      const numericTree = Number(categoryTreeId);
      const url = Number.isFinite(numericTree) ? `${base}${base.includes('?') ? '&' : '?'}categoryTreeId=${numericTree}` : base;
      const res = await axios.get(url);
      dispatch(slice.actions.setItemConditionsByAccount({ key, conditions: res.data?.conditions || [] }));
    } catch (error) {
      console.error(`Failed to fetch item conditions for ${key}:`, error);
    }
  };
}

/**
 * eBay Motors parts compatibility (fitment): fetch the vehicle aspect NAMES for a category and whether
 * it supports compatibility at all. Keyed by `${integrationId}:${categoryId}`; stores { supported, properties }.
 * Never throws to the UI — on failure it stores supported:false so the fitment editor simply hides.
 */
export function getCompatibilityPropertiesForAccount (integrationId, categoryId, categoryTreeId = 100) {
  return async (dispatch, getState) => {
    const key = `${integrationId}:${categoryId}`;
    if (getState().management.compatibilityByAccount[key]) return;
    if (!categoryId) return;
    try {
      const res = await axios.post(withIntegrationId(endpoints.management.ebay.compatibilityProperties, integrationId), {
        category_id: categoryId,
        categoryTreeId,
      });
      dispatch(slice.actions.setCompatibilityByAccount({
        key,
        supported: !!res?.data?.supported,
        properties: res?.data?.properties || [],
      }));
    } catch (error) {
      console.error(`Failed to fetch eBay compatibility properties for ${key}:`, error);
      dispatch(slice.actions.setCompatibilityByAccount({ key, supported: false, properties: [] }));
    }
  };
}

/**
 * eBay Motors parts compatibility: fetch valid VALUES for one vehicle aspect, optionally filtered by
 * prior selections, to power cascading Make → Model → Year dropdowns. Returns the values array (the
 * caller manages cascade state locally — no global cache needed).
 */
export function getCompatibilityValuesForAccount (integrationId, categoryId, property, filters, categoryTreeId = 100) {
  return async () => {
    if (!categoryId || !property) return [];
    try {
      const res = await axios.post(withIntegrationId(endpoints.management.ebay.compatibilityValues, integrationId), {
        category_id: categoryId,
        property,
        filters,
        categoryTreeId,
      });
      return res?.data?.values || [];
    } catch (error) {
      console.error(`Failed to fetch eBay compatibility values (${property}) for ${integrationId}:${categoryId}:`, error);
      return [];
    }
  };
}

/**
 * Fetch policies for a specific account (eBay or Etsy).
 * Keyed by integrationId — each account has its own policies.
 */
export function fetchPoliciesForAccount (integrationId, platform, { force = false } = {}) {
  return async (dispatch, getState) => {
    // Dedup: skip if already fetched (unless forced)
    if (!force && getState().management.policiesByAccount[integrationId]) return;

    dispatch(slice.actions.setAccountLoading({ integrationId, field: 'policies', loading: true }));
    try {
      let url;
      if (platform === 'ebay') {
        url = withIntegrationId(endpoints.management.ebay.policies.get, integrationId);
      } else if (platform === 'etsy') {
        url = withIntegrationId(endpoints.management.etsy.policies.get, integrationId);
      } else {
        return;
      }
      const { data } = await axios.get(url);
      dispatch(slice.actions.setPoliciesByAccount({ integrationId, policies: data }));
    } catch (error) {
      console.error(`Failed to fetch policies for account ${integrationId}:`, error);
    } finally {
      dispatch(slice.actions.setAccountLoading({ integrationId, field: 'policies', loading: false }));
    }
  };
}

